import { useState, useEffect, useMemo, useCallback, RefObject } from "react";
import { createPortal } from "react-dom";
import { Avatar } from "../../../../../../components/Avatar/Avatar";
import { visibleScrollbar } from "../../../../../../utils/functions/get-visible-scrollbar";
import CheckIcon from "../../../../../../assets/svg/check-icon";
import SearchIcon from "../../../../../../assets/svg/search-icon";
import Button from "../../../../../../components/Button/Button";
import { useGetAllAgentsQuery } from "../../../../../../redux/services/userManagementApi";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../redux/store";
import { useFilterPortalPosition } from "./useFilterPortalPosition";

interface AgentItem {
  id: string;
  name: string;
  email: string;
}

interface ContactOwnerFilterProps {
  selectedAgentIds: string[];
  onApply: (ids: string[]) => void;
  onReset: () => void;
  onClose: () => void;
  triggerRef: RefObject<HTMLDivElement | null>;
  headingText?: string;
  customAgents?: AgentItem[];
  maxHeight?: string;
  hideUnassigned?: boolean;
  isAgentFilter?: boolean;
}

export const ContactOwnerFilter = ({
  selectedAgentIds,
  onApply,
  onReset,
  onClose,
  triggerRef,
  headingText = "Contact Owner",
  customAgents,
  maxHeight,
  hideUnassigned = false,
  isAgentFilter = true,
}: ContactOwnerFilterProps) => {
  const pos = useFilterPortalPosition(triggerRef);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSelected, setTempSelected] = useState<Set<string>>(
    new Set(selectedAgentIds)
  );

  const userId = useSelector((state: RootState) => state.auth.user?._id);
  const { data: allAgentsData, isFetching } = useGetAllAgentsQuery(undefined, {
    skip: !!customAgents,
  });

  const agents: AgentItem[] = useMemo(() => {
    if (customAgents) return customAgents;
    return (
      allAgentsData?.items?.map(
        (u: { _id: string; name: string; email: string }) => ({
          id: u._id,
          name: `${u.name}${u._id === userId ? " (Me)" : ""}`,
          email: u.email,
        })
      ) || []
    );
  }, [customAgents, allAgentsData, userId]);

  useEffect(() => {
    setTempSelected(new Set(selectedAgentIds));
  }, [selectedAgentIds]);

  const filteredAgents = searchQuery.trim()
    ? agents.filter(
        (a) =>
          a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          a.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : agents;

  const selectedAgents = agents.filter((a) => tempSelected.has(a.id));
  const unselectedAgents = filteredAgents.filter(
    (a) => !tempSelected.has(a.id)
  );

  const handleToggle = useCallback((agentId: string) => {
    setTempSelected((prev) => {
      if (agentId === "unassigned") {
        return prev.has("unassigned") ? new Set() : new Set(["unassigned"]);
      }
      const next = new Set(prev);
      next.delete("unassigned");
      if (next.has(agentId)) {
        next.delete(agentId);
      } else {
        next.add(agentId);
      }
      return next;
    });
  }, []);

  const handleApply = useCallback(() => {
    onApply(Array.from(tempSelected));
  }, [tempSelected, onApply]);

  const hasChanges =
    tempSelected.size !== selectedAgentIds.length ||
    !Array.from(tempSelected).every((id) => selectedAgentIds.includes(id));

  const type = isAgentFilter ? "agents" : "users";

  if (!pos) return null;

  return createPortal(
    <div
      className="fixed z-[20001] w-[280px] bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg shadow-popper-blur-effect backdrop-blur-[20px] flex flex-col overflow-hidden"
      style={{ top: pos.top, left: pos.left, maxHeight: maxHeight ?? "400px" }}
      data-table-filter-dropdown
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 pb-2">
        <span className="text-xs font-semibold text-verba-indigo dark:text-verba-indigo-dk uppercase tracking-wide">
          {headingText}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onReset();
          }}
          className="text-sm font-medium text-verba-blue dark:text-verba-blue-dk hover:underline"
        >
          Reset
        </button>
      </div>

      {/* Search */}
      <div className="pb-2">
        <div className="relative">
          <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search"
            className="w-full py-2 pl-7 pr-2 text-sm text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk border-b border-border-gray dark:border-border-gray-dk focus:outline-none bg-transparent"
          />
        </div>
      </div>

      {/* Agent list */}
      <div
        className={`flex-1 overflow-y-auto ${visibleScrollbar(
          "[&::-webkit-scrollbar]:w-[4px]"
        )}`}
      >
        {isFetching ? (
          <div className="px-3 py-3 text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
            Loading {type}...
          </div>
        ) : (
          <>
            {/* Selected agents */}
            {selectedAgents.length > 0 && (
              <div className="divide-y divide-border-gray dark:divide-border-gray-dk">
                {selectedAgents.map((agent) => (
                  <button
                    key={agent.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(agent.id);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 bg-card-highlight-2 dark:bg-card-highlight-2-dk"
                  >
                    <div
                      className={`flex items-center justify-center w-[14px] h-[14px] border rounded-[3px] shrink-0 bg-verba-blue text-fixed-white border-verba-blue dark:bg-verba-blue-dk dark:border-verba-blue-dk`}
                    >
                      <CheckIcon className="w-3 h-3 text-fixed-white dark:text-fixed-white-dk" />
                    </div>
                    <Avatar text={agent.name} maxChars={1} size={24} />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk truncate">
                        {agent.name}
                      </p>
                      <p className="text-xs text-dark-gray dark:text-dark-gray-dk truncate">
                        {agent.email}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* ALL USERS header */}
            <div className="px-3 pt-1 text-xxs text-left font-semibold text-dark-gray dark:text-dark-gray-dk uppercase">
              All Users
            </div>

            {/* Unselected agents */}
            {unselectedAgents.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {searchQuery.trim()
                  ? `No ${type} found`
                  : `No ${type} available`}
              </div>
            ) : (
              <div className="divide-y divide-border-gray dark:divide-border-gray-dk">
                {unselectedAgents.map((agent) => (
                  <button
                    key={agent.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(agent.id);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                  >
                    <div className="flex items-center justify-center w-[14px] h-[14px] border rounded-[3px] shrink-0 border-gray-100 dark:border-gray-100-dk" />
                    <Avatar text={agent.name} maxChars={1} size={24} />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm text-verba-indigo dark:text-verba-indigo-dk truncate">
                        {agent.name}
                      </p>
                      <p className="text-xs text-dark-gray dark:text-dark-gray-dk truncate">
                        {agent.email}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Unassigned option */}
            {!hideUnassigned && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggle("unassigned");
                }}
                className="w-full flex items-center justify-between px-3 py-2.5 border-t border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
              >
                <span className="text-sm text-verba-indigo dark:text-verba-indigo-dk">
                  Unassigned
                </span>
                <div
                  className={`flex items-center justify-center w-[14px] h-[14px] border rounded-[3px] shrink-0 ${
                    tempSelected.has("unassigned")
                      ? "bg-verba-blue text-fixed-white border-verba-blue dark:bg-verba-blue-dk dark:border-verba-blue-dk"
                      : "border-gray-100 dark:border-gray-100-dk"
                  }`}
                >
                  {tempSelected.has("unassigned") && (
                    <CheckIcon className="w-3 h-3 text-fixed-white dark:text-fixed-white-dk" />
                  )}
                </div>
              </button>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-t border-border-gray dark:border-border-gray-dk">
        <Button
          innerText="Cancel"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          innerTextClass="text-heading-text dark:text-heading-text-dk text-md font-semibold"
          buttonContainerClass="min-w-[80px] px-2 py-1.5 rounded-lg hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
        />
        <Button
          innerText="Apply"
          onClick={(e) => {
            e.stopPropagation();
            handleApply();
          }}
          disabled={!hasChanges}
          innerTextClass="text-md font-semibold text-pure-white dark:text-pure-white-dk"
          buttonContainerClass="min-w-[80px] px-2 py-1.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-25 disabled:cursor-not-allowed"
        />
      </div>
    </div>,
    document.body
  );
};
